import type { ReactNode } from "react";
import { Clock } from "./icons";

/**
 * A refund that **has not reached the provider** (`009` R2).
 *
 * The provider could not be reached, so nothing is known about the outcome and the money is still
 * owed. Nothing has been refused. The Accounts user's next action is to wait or to escalate the
 * connection — not to fix a record and retry.
 *
 * ## Why this is not `<RefundStatus status="queued" />`
 *
 * `RefundFailed` is its sibling, in its own file, on purpose. A single component with a status prop
 * is smaller, compiles, and is how the distinction gets flattened by the second person to touch it:
 * an interpolated string and a colour map lose the fact that the two states mean **opposite things**
 * about what the operator should do next. The contract is explicit about the difference, so the
 * component set is too.
 *
 * ## No retry control
 *
 * The schedule is the API's. Retrying an unanswered call on a human's impatience is how a gateway
 * starts rate-limiting the portal. `attempts` and `nextAttempt` are the API's figures, rendered.
 */
export function RefundQueued({
  amount,
  attempts,
  nextAttempt,
  children,
}: {
  /** The refund amount, already formatted by the caller (§8.5, constitution I). */
  amount: ReactNode;
  /** The API's attempt count, as text — "3 of 6". Never counted here. */
  attempts: ReactNode;
  /** When the API says it will try again. Its wording, its clock (`002` R3). */
  nextAttempt: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div
      data-slot="refund-queued"
      className="border-orange/45 bg-orange/10 text-foreground flex gap-3.5 rounded-xl border p-4 text-sm"
    >
      <span className="bg-orange/15 text-orange-dark mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg">
        <Clock aria-hidden className="size-4" />
      </span>

      <div className="flex min-w-0 flex-col gap-1.5">
        <p className="font-semibold">Queued — this refund has not reached the provider.</p>
        <p className="text-muted-foreground">
          Nothing has been refused. The provider could not be reached, so the outcome is not known
          and {amount} is still owed to the customer.
        </p>
        <dl className="text-muted-foreground flex flex-wrap gap-x-6 gap-y-1 pt-0.5 text-xs">
          <div className="flex gap-1.5">
            <dt>Attempts</dt>
            <dd className="text-foreground font-medium">{attempts}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt>Next attempt</dt>
            <dd className="text-foreground font-medium">{nextAttempt}</dd>
          </div>
        </dl>
        {children ? <div className="pt-1">{children}</div> : null}
      </div>
    </div>
  );
}
